import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { User } from 'firebase/auth';
import {
  likeWatchlist as functionsLikeWatchlist,
  getLikeStatus,
  SocialActionResult,
  LikeStatusResult,
  shouldUseFunctionsAPI
} from '../services/functionsApi';
import { socialEventBroadcaster, SocialEventData } from '../services/socialEventBroadcaster';
import { authService, movieService, isFirebaseInitialized, watchlistService, socialService, getStandardizedMovieId } from '@watchlist/shared';
import { firebaseService } from '../services/firebaseService';
import { isCapacitorNative, getWatchlistQueryTimeout } from '../utils/platformUtils';
import { touchWatchlistRecency } from '../utils/watchlistRecency';
import { getCachedUserProfile, batchFetchProfiles, getProfileFromCache } from '../utils/profileCache';
import {
  getCachedLikedWatchlists,
  invalidateLikedWatchlistsCache,
  addToLikedWatchlistsCache,
  removeFromLikedWatchlistsCache
} from '../utils/likedWatchlistsCache';
import { invalidateWatchlistLikeCountCache } from '../utils/watchlistLikeCountResolver';

// Types for watchlist data
export interface Movie {
  id: string;
  title: string;
  year?: string;
  poster?: string;
  imdbID?: string;
  genre?: string;
  director?: string;
  plot?: string;
  rating?: string;
  addedAt: string;
  addedBy?: string;
  note?: string;
}

export interface CollaboratorProfile {
  uid: string;
  name: string;
  avatar?: string;
  email?: string;
  role?: 'admin' | 'editor'; // Optional for backward compatibility
  status?: 'confirmed' | 'pending'; // For request system
  addedAt?: string;
}

function applyCollaboratorAdminRoles(
  profiles: CollaboratorProfile[],
  adminCollaboratorIds?: string[]
): CollaboratorProfile[] {
  const adminIds = new Set(adminCollaboratorIds || []);
  return profiles.map(p => ({
    ...p,
    role: adminIds.has(p.uid) ? ('admin' as const) : ('editor' as const)
  }));
}

export interface Watchlist {
  id: string;
  name: string;
  description?: string;
  movieDetails: Movie[]; // Standardized from 'movies' to 'movieDetails'
  isPrivate: boolean;
  createdBy: string;
  createdByName: string;
  createdByAvatar?: string;
  createdAt: string;
  updatedAt: string;
  collaborators?: string[];
  adminCollaboratorIds?: string[];
  collaboratorNames?: string[];
  collaboratorProfiles?: CollaboratorProfile[]; // Enhanced collaborator info
  followerCount: number;
  viewCount?: number;
  isFollowing?: boolean;
  isCollaborative?: boolean; // True if current user is a collaborator (not owner)
  tags?: string[];
  coverImage?: string;
}

export interface UserWatchlistStats {
  totalWatchlists: number;
  totalMovies: number;
  totalFollowing: number;
  totalFollowers: number;
  recentlyAdded: Movie[];
  topGenres: string[];
}

interface UserWatchlistDataContextType {
  // State
  userWatchlists: Watchlist[];
  likedWatchlists: Watchlist[];
  allWatchlists: Watchlist[];
  stats: UserWatchlistStats;
  isLoading: boolean;
  likedWatchlistsReady: boolean;
  error: string | null;
  defaultWatchlistSettings: any;

  // User's own watchlists
  createWatchlist: (watchlist: Omit<Watchlist, 'id' | 'createdAt' | 'updatedAt' | 'followerCount'>, shareToFeed?: boolean) => Promise<string>;
  updateWatchlist: (id: string, updates: Partial<Watchlist>) => Promise<void>;
  deleteWatchlist: (id: string) => Promise<void>;

  // Movie management
  addMovieToWatchlist: (watchlistId: string, movie: Omit<Movie, 'addedAt' | 'addedBy'>) => Promise<void>;
  removeMovieFromWatchlist: (watchlistId: string, movieId: string) => Promise<void>;
  moveMovieBetweenWatchlists: (movieId: string, fromWatchlistId: string, toWatchlistId: string) => Promise<void>;
  updateMovieNote: (watchlistId: string, movieId: string, note: string) => Promise<void>;

  // Liking/unliking watchlists
  likeWatchlist: (watchlistId: string) => Promise<void>;
  unlikeWatchlist: (watchlistId: string) => Promise<void>;

  // Collaboration
  addCollaborator: (watchlistId: string, userId: string, userName: string) => Promise<void>;
  removeCollaborator: (watchlistId: string, userId: string) => Promise<void>;
  getCollaborativeWatchlists: () => Promise<Watchlist[]>;
  canUserEditWatchlist: (watchlistId: string) => Promise<boolean>;

  // Data fetching
  refreshData: () => Promise<void>;
  getWatchlistById: (id: string) => Watchlist | null;
  searchWatchlists: (query: string) => Watchlist[];
  getWatchlistsByGenre: (genre: string) => Watchlist[];

  // Analytics
  getMovieRecommendations: (watchlistId?: string) => Movie[];
  getSimilarWatchlists: (watchlistId: string) => Watchlist[];

  // Debug and sync functions
  syncWatchlistData: (watchlistId: string) => Promise<void>;

  // Default watchlist settings
  getDefaultWatchlistSettings: (watchlistType: string) => any;
  updateDefaultWatchlistSettings: (watchlistType: string, updates: any) => Promise<void>;
}

const UserWatchlistDataContext = createContext<UserWatchlistDataContextType | undefined>(undefined);

interface UserWatchlistDataProviderProps {
  children: ReactNode;
  user?: User | null;
  isFirebaseReady?: boolean;
}

export const UserWatchlistDataProvider: React.FC<UserWatchlistDataProviderProps> = ({
  children,
  user,
  isFirebaseReady = false
}) => {
  const [userWatchlists, setUserWatchlists] = useState<Watchlist[]>([]);
  const [likedWatchlists, setLikedWatchlists] = useState<Watchlist[]>([]);
  const [likedWatchlistsReady, setLikedWatchlistsReady] = useState(false);
  const [allWatchlists, setAllWatchlists] = useState<Watchlist[]>([]);
  const [stats, setStats] = useState<UserWatchlistStats>({
    totalWatchlists: 0,
    totalMovies: 0,
    totalFollowing: 0,
    totalFollowers: 0,
    recentlyAdded: [],
    topGenres: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [defaultWatchlistSettings, setDefaultWatchlistSettings] = useState<any>({});

  // CRITICAL: Track if we've already loaded data for the current user to prevent double-loading
  const dataLoadedForUserRef = useRef<string | null>(null);
  const isLoadingRef = useRef(false);

  const loadLocalData = useCallback(() => {
    try {
      const localWatchlists = localStorage.getItem('userWatchlists');
      const localLiked = localStorage.getItem('likedWatchlists');

      if (localWatchlists) {
        setUserWatchlists(JSON.parse(localWatchlists));
      }

      if (localLiked) {
        setLikedWatchlists(JSON.parse(localLiked));
      }

      updateStats();
    } catch (error) {
      console.error('Error loading local watchlist data:', error);
      setError('Failed to load local data');
    } finally {
      setIsLoading(false);
      setLikedWatchlistsReady(true);
    }
  }, []);

  const saveLocalData = useCallback(() => {
    try {
      localStorage.setItem('userWatchlists', JSON.stringify(userWatchlists));
      localStorage.setItem('likedWatchlists', JSON.stringify(likedWatchlists));
    } catch (error) {
      console.error('Error saving local watchlist data:', error);
    }
  }, [userWatchlists, likedWatchlists]);

  // Helper function to resolve collaborator profiles - uses cached profile fetching for performance
  const resolveCollaboratorProfiles = useCallback(async (collaboratorUids: string[]): Promise<CollaboratorProfile[]> => {
    if (!collaboratorUids || collaboratorUids.length === 0) return [];

    try {
      // Batch fetch all profiles first (this deduplicates and caches)
      await batchFetchProfiles(collaboratorUids);

      // Now map from cache
      const profiles = await Promise.all(
        collaboratorUids.map(async (uid: string) => {
          const profile = await getCachedUserProfile(uid);
          return {
            uid,
            name: profile?.displayName || 'Unknown User',
            avatar: profile?.photoURL,
            email: profile?.email
          };
        })
      );
      return profiles;
    } catch (error) {
      return [];
    }
  }, []);

  const refreshData = useCallback(async () => {
    if (!user) {
      loadLocalData();
      return;
    }

    // Check if Firebase is properly initialized before attempting operations
    if (!isFirebaseInitialized()) {
      console.warn('UserWatchlistDataContext: Firebase not yet initialized, skipping data refresh');
      return;
    }

    setIsLoading(true);
    setLikedWatchlistsReady(false);
    setError(null);

    try {
      console.log('UserWatchlistDataContext: Refreshing data for user:', user.uid);

      // Add timeout for iOS native Firestore (slower than web)
      // Using centralized platform detection for consistency
      const watchlistTimeout = getWatchlistQueryTimeout();
      console.log(`UserWatchlistDataContext: Using ${watchlistTimeout}ms timeout (native: ${isCapacitorNative()})`);

      // Load user's own watchlists with timeout
      const userListsTimeoutPromise = new Promise<any[]>((resolve) => {
        setTimeout(() => {
          console.warn(`UserWatchlistDataContext: getUserWatchlists timeout after ${watchlistTimeout / 1000} seconds, returning empty array`);
          resolve([]);
        }, watchlistTimeout);
      });

      const userLists = await Promise.race([
        watchlistService.getUserWatchlists(user.uid),
        userListsTimeoutPromise
      ]);
      console.log('UserWatchlistDataContext: Loaded user watchlists:', userLists.length);

      // Debug each watchlist structure and filter out test data
      userLists.forEach((list, index) => {
        console.log(`UserWatchlistDataContext: Watchlist ${index + 1} - "${list.name}" (ID: ${list.id}):`, {
          id: list.id,
          movieDetailsArray: list.movieDetails?.length || 0,
          movieDetailsArrayCast: (list as any).movieDetails?.length || 0,
          movieDetailsData: list.movieDetails,
          movieDetailsDataCast: (list as any).movieDetails
        });
      });

      // Use all user lists without filtering
      const filteredUserLists = userLists;

      // Load collaborative watchlists with timeout
      const collaborativeTimeoutPromise = new Promise<any[]>((resolve) => {
        setTimeout(() => {
          console.warn(`UserWatchlistDataContext: getCollaborativeWatchlists timeout after ${watchlistTimeout / 1000} seconds, returning empty array`);
          resolve([]);
        }, watchlistTimeout);
      });

      const collaborativeLists = await Promise.race([
        watchlistService.getCollaborativeWatchlists(user.uid),
        collaborativeTimeoutPromise
      ]);
      console.log('UserWatchlistDataContext: Loaded collaborative watchlists:', collaborativeLists.length);

      // Load liked watchlists (using cache to prevent redundant calls)
      const likedLists = await getCachedLikedWatchlists(user.uid);
      console.log('UserWatchlistDataContext: Loaded liked watchlists:', likedLists.length);

      // Load public watchlists for discovery
      const publicLists = await watchlistService.getPublicWatchlists(50);
      console.log('UserWatchlistDataContext: Loaded public watchlists:', publicLists.length);

      // Convert Firebase watchlists to our format with proper movie details
      const convertedLists: Watchlist[] = await Promise.all(
        filteredUserLists.map(async (list) => {
          // Initialize movieDetails array for this watchlist
          let movieDetails: Movie[] = [];

          // Check if watchlist has movieDetails array
          if (list.movieDetails && Array.isArray(list.movieDetails)) {
            movieDetails = list.movieDetails
              .filter((movie: any) => {
                const isValid = movie && (movie.id || movie.imdbID) && (movie.title || movie.Title);
                if (!isValid) {
                  console.warn('UserWatchlistDataContext: Filtering out invalid movie:', movie);
                }
                return isValid;
              })
              .map((movie: any) => {
                // Standardize movie data format to ensure consistent field names
                return {
                  ...movie,
                  // Ensure consistent field names for UI compatibility
                  Title: movie.Title || movie.title || 'Unknown Movie',
                  title: movie.title || movie.Title || 'Unknown Movie',
                  Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                  year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                  Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                  poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
                  imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                  id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                };
              });
          } else {
            console.log('UserWatchlistDataContext: Watchlist', list.name, 'has no movieDetails or empty movieDetails array');
          }

          // Resolve collaborator profiles
          const collaboratorProfiles = applyCollaboratorAdminRoles(
            await resolveCollaboratorProfiles(list.collaborators || []),
            list.adminCollaboratorIds
          );

          console.log('UserWatchlistDataContext: Final movieDetails count for', list.name, ':', movieDetails.length);

          return {
            id: list.id,
            name: list.name,
            description: list.description || '',
            movieDetails,
            isPrivate: list.isPrivate,
            createdBy: list.createdBy,
            createdByName: user.displayName || 'You',
            createdAt: list.createdAt,
            updatedAt: list.updatedAt,
            collaborators: list.collaborators || [],
            adminCollaboratorIds: list.adminCollaboratorIds,
            collaboratorNames: collaboratorProfiles.map(p => p.name),
            collaboratorProfiles,
            followerCount: list.followerCount || 0, // Show actual like count from Firebase
            viewCount: (list as any).viewCount || 0,
            tags: [],
            isFollowing: false // User doesn't follow their own lists
          } as Watchlist;
        })
      );

      // Convert collaborative watchlists with creator profile fetching
      const convertedCollaborativeLists: Watchlist[] = await Promise.all(
        collaborativeLists.map(async list => {
          // Try to get actual user profile for creator info
          let createdByName = list.createdBy;
          let createdByAvatar: string | undefined = undefined;

          try {
            const creatorProfile = await getCachedUserProfile(list.createdBy);
            if (creatorProfile) {
              createdByName = creatorProfile.displayName || creatorProfile.email?.split('@')[0] || 'Unknown User';
              createdByAvatar = creatorProfile.photoURL;
            }
          } catch (error) {
            console.warn('Failed to fetch creator profile for:', list.createdBy);
          }

          // Initialize movieDetails array for this watchlist
          let movieDetails: Movie[] = [];

          // Check if watchlist has movieDetails array
          if (list.movieDetails && Array.isArray(list.movieDetails)) {
            movieDetails = list.movieDetails
              .filter((movie: any) => {
                const isValid = movie && (movie.id || movie.imdbID) && (movie.title || movie.Title);
                if (!isValid) {
                  console.warn('UserWatchlistDataContext: Filtering out invalid movie in collaborative list:', movie);
                }
                return isValid;
              })
              .map((movie: any) => {
                // Standardize movie data format to ensure consistent field names
                return {
                  ...movie,
                  // Ensure consistent field names for UI compatibility
                  Title: movie.Title || movie.title || 'Unknown Movie',
                  title: movie.title || movie.Title || 'Unknown Movie',
                  Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                  year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                  Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                  poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
                  imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                  id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                };
              });
          }

          // Resolve collaborator profiles (excluding current user since they're already a collaborator)
          const collaboratorProfiles = applyCollaboratorAdminRoles(
            await resolveCollaboratorProfiles(list.collaborators || []),
            list.adminCollaboratorIds
          );

          return {
            id: list.id,
            name: list.name,
            description: list.description || '',
            movieDetails,
            isPrivate: list.isPrivate,
            createdBy: list.createdBy,
            createdByName,
            createdByAvatar,
            createdAt: list.createdAt,
            updatedAt: list.updatedAt,
            collaborators: list.collaborators || [],
            adminCollaboratorIds: list.adminCollaboratorIds,
            collaboratorNames: collaboratorProfiles.map(p => p.name),
            collaboratorProfiles,
            followerCount: list.followerCount || 0,
            viewCount: (list as any).viewCount || 0,
            tags: [],
            isFollowing: false // This is a collaborative list, not followed
          };
        })
      );

      // Merge user's own watchlists with collaborative ones
      const allUserWatchlists = [...convertedLists, ...convertedCollaborativeLists];

      // OPTIMIZATION: Batch preload all creator profiles before mapping
      // This prevents N individual requests when mapping N watchlists
      const allCreatorIds = [
        ...likedLists.map(list => list.createdBy),
        ...publicLists.filter(list => list.createdBy !== user.uid).map(list => list.createdBy)
      ];
      const uniqueCreatorIds = [...new Set(allCreatorIds.filter(id => id))];
      if (uniqueCreatorIds.length > 0) {
        console.log(`📦 UserWatchlistDataContext: Batch preloading ${uniqueCreatorIds.length} creator profiles`);
        await batchFetchProfiles(uniqueCreatorIds);
      }

      // Convert liked watchlists with creator profile fetching (profiles now cached)
      const convertedLikedLists: Watchlist[] = await Promise.all(
        likedLists.map(async list => {
          // Try to get actual user profile for creator info (should be cached from batch fetch)
          let createdByName = list.createdBy;
          let createdByAvatar: string | undefined = undefined;

          try {
            const creatorProfile = await getCachedUserProfile(list.createdBy);
            if (creatorProfile) {
              createdByName = creatorProfile.displayName || creatorProfile.email?.split('@')[0] || 'Unknown User';
              createdByAvatar = creatorProfile.photoURL;
            }
          } catch (error) {
            console.warn('Failed to fetch creator profile for:', list.createdBy);
          }

          // Process and validate movie details for liked watchlists (same as user watchlists)
          let movieDetails: Movie[] = [];

          if (list.movieDetails && Array.isArray(list.movieDetails)) {
            movieDetails = list.movieDetails
              .filter((movie: any) => {
                const isValid = movie && (movie.id || movie.imdbID) && (movie.title || movie.Title);
                if (!isValid) {
                  console.warn('UserWatchlistDataContext: Filtering out invalid movie from liked watchlist:', movie);
                }
                return isValid;
              })
              .map((movie: any) => {
                // Standardize movie data format to ensure consistent field names
                return {
                  ...movie,
                  // Ensure consistent field names for UI compatibility
                  Title: movie.Title || movie.title || 'Unknown Movie',
                  title: movie.title || movie.Title || 'Unknown Movie',
                  Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                  year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                  Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                  poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
                  imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                  id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                };
              });
          } else {
            console.log('UserWatchlistDataContext: Liked watchlist', list.name, 'has no movieDetails or empty movieDetails array');
          }

          return {
            id: list.id,
            name: list.name,
            description: list.description || '',
            movieDetails,
            isPrivate: list.isPrivate,
            createdBy: list.createdBy,
            createdByName,
            createdByAvatar,
            createdAt: list.createdAt,
            updatedAt: list.updatedAt,
            collaborators: list.collaborators || [],
            collaboratorNames: list.collaborators || [],
            followerCount: list.followerCount || 0,
            viewCount: (list as any).viewCount || 0,
            tags: [],
            isFollowing: true
          };
        })
      );

      // Convert public watchlists and mark liked status with creator profile fetching
      const likedIds = new Set(likedLists.map(list => list.id));
      const convertedPublicLists: Watchlist[] = await Promise.all(
        publicLists
          .filter(list => list.createdBy !== user.uid) // Exclude user's own lists
          .map(async list => {
            // Try to get actual user profile for creator info
            let createdByName = list.createdBy;
            let createdByAvatar: string | undefined = undefined;

            try {
              const creatorProfile = await getCachedUserProfile(list.createdBy);
              if (creatorProfile) {
                createdByName = creatorProfile.displayName || creatorProfile.email?.split('@')[0] || 'Unknown User';
                createdByAvatar = creatorProfile.photoURL;
              }
            } catch (error) {
              console.warn('Failed to fetch creator profile for:', list.createdBy);
            }

            // Process and validate movie details for public watchlists (same as user watchlists)
            let movieDetails: Movie[] = [];

            if (list.movieDetails && Array.isArray(list.movieDetails)) {
              movieDetails = list.movieDetails
                .filter((movie: any) => {
                  const isValid = movie && (movie.id || movie.imdbID) && (movie.title || movie.Title);
                  if (!isValid) {
                    console.warn('UserWatchlistDataContext: Filtering out invalid movie from public watchlist:', movie);
                  }
                  return isValid;
                })
                .map((movie: any) => {
                  // Standardize movie data format to ensure consistent field names
                  return {
                    ...movie,
                    // Ensure consistent field names for UI compatibility
                    Title: movie.Title || movie.title || 'Unknown Movie',
                    title: movie.title || movie.Title || 'Unknown Movie',
                    Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                    year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                    Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                    poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
                    imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                    id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                  };
                });
            } else {
              console.log('UserWatchlistDataContext: Public watchlist', list.name, 'has no movieDetails or empty movieDetails array');
            }

            return {
              id: list.id,
              name: list.name,
              description: list.description || '',
              movieDetails,
              isPrivate: list.isPrivate,
              createdBy: list.createdBy,
              createdByName,
              createdByAvatar,
              createdAt: list.createdAt,
              updatedAt: list.updatedAt,
              collaborators: list.collaborators || [],
              collaboratorNames: list.collaborators || [],
              followerCount: list.followerCount || 0,
              viewCount: (list as any).viewCount || 0,
              tags: [],
              isFollowing: likedIds.has(list.id)
            };
          })
      );

      console.log('UserWatchlistDataContext: Setting converted lists:', {
        userWatchlists: convertedLists.length,
        collaborativeWatchlists: convertedCollaborativeLists.length,
        totalUserWatchlists: allUserWatchlists.length,
        likedWatchlists: convertedLikedLists.length,
        allWatchlists: convertedPublicLists.length
      });

      // Enhanced logging for each converted list to debug empty movie issues
      allUserWatchlists.forEach((list, index) => {
        console.log(`UserWatchlistDataContext: Final converted list ${index + 1} - ${list.name}:`, {
          id: list.id,
          isCollaborative: list.createdBy !== user.uid,
          createdBy: list.createdBy,
          createdByName: list.createdByName,
          moviesCount: list.movieDetails.length,
          firstFewMovies: list.movieDetails.slice(0, 3).map(m => ({ id: m.id, title: m.title, imdbID: m.imdbID }))
        });
      });

      setUserWatchlists(allUserWatchlists);
      setLikedWatchlists(convertedLikedLists);
      setLikedWatchlistsReady(true);
      setAllWatchlists(convertedPublicLists);

      updateStats();

      console.log('UserWatchlistDataContext: Data refresh completed successfully');
    } catch (error) {
      console.error('UserWatchlistDataContext: Error refreshing data:', error);
      setError('Failed to load watchlist data');
    } finally {
      setIsLoading(false);
      setLikedWatchlistsReady(true);
    }
  }, [user, loadLocalData]);

  // Initialize data when user changes - with real-time updates
  useEffect(() => {
    if (user && isFirebaseReady) {
      // CRITICAL: Prevent double-loading for the same user
      // This happens because the effect can run multiple times due to auth state changes
      if (dataLoadedForUserRef.current === user.uid && !isLoading) {
        console.log('UserWatchlistDataContext: Data already loaded for user, skipping re-init:', user.uid);
        return;
      }

      // Prevent concurrent loads
      // CRITICAL FIX: If we're "loading" but the previous effect run was cancelled,
      // we need to allow this new run to proceed. Reset the loading ref.
      if (isLoadingRef.current) {
        console.log('UserWatchlistDataContext: Previous load was likely cancelled by re-render, resetting loading state');
        isLoadingRef.current = false;
      }

      // Set up real-time listener for user's watchlists
      console.log('UserWatchlistDataContext: Setting up real-time listener for user:', user.uid, '(Firebase ready:', isFirebaseReady, ')');

      let isInitialLoad = true;
      let unsubscribeOwned: (() => void) | null = null;
      let unsubscribeCollaborative: (() => void) | null = null;
      let isCancelled = false;

      // Store both owned and collaborative watchlists
      let ownedWatchlists: Watchlist[] = [];
      let collaborativeWatchlistsData: Watchlist[] = [];

      // Function to merge and update state
      const mergeWatchlists = () => {
        if (isCancelled) return;
        const allUserWatchlists = [...ownedWatchlists, ...collaborativeWatchlistsData];
        setUserWatchlists(allUserWatchlists);
        updateStats();
      };

      // Async function to initialize listeners after Firebase is ready
      const initializeListeners = async () => {
        // CRITICAL FIX: Don't double-check Firebase initialization here
        // The parent effect already checks isFirebaseReady, and we trust that flag
        // Double-checking with isFirebaseInitialized() can cause sync issues on Android
        // where AuthContext sets isFirebaseReady but FirebaseManager might not be ready yet

        if (isCancelled) {
          console.log('UserWatchlistDataContext: Listener setup cancelled (user changed)');
          return;
        }

        // Mark as loading
        isLoadingRef.current = true;

        // CRITICAL FIX: On native platforms (iOS/Android), the web SDK's onSnapshot doesn't work
        // with the Capacitor Firestore plugin. Skip subscriptions and use direct queries instead.
        if (isCapacitorNative()) {
          console.log('UserWatchlistDataContext: Native platform detected - using direct queries instead of subscriptions');
          await new Promise(resolve => setTimeout(resolve, 300)); // Small delay for Firestore stability

          // CRITICAL FIX: Helper to race a promise against a timeout
          // On native, Firestore/profile queries can hang indefinitely
          const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, fallback: T, label: string): Promise<T> => {
            return Promise.race([
              promise,
              new Promise<T>((resolve) => {
                setTimeout(() => {
                  console.warn(`⏰ UserWatchlistDataContext: ${label} timed out after ${timeoutMs}ms, using fallback`);
                  resolve(fallback);
                }, timeoutMs);
              })
            ]);
          };

          try {
            // Load owned watchlists directly with timeout
            const [ownedLists, collaborativeLists] = await withTimeout(
              Promise.all([
                watchlistService.getUserWatchlists(user.uid),
                watchlistService.getCollaborativeWatchlists(user.uid)
              ]),
              15000,
              [[], []] as [any[], any[]],
              'getUserWatchlists+getCollaborativeWatchlists'
            );

            console.log('UserWatchlistDataContext: Native query got', ownedLists.length, 'owned,', collaborativeLists.length, 'collaborative watchlists');

            if (isCancelled) {
              // Data arrived but effect was cancelled (likely a re-render)
              // Still set the data if the user is the same - the next effect run
              // will see dataLoadedForUserRef and skip
              console.log('UserWatchlistDataContext: Effect cancelled after data arrived, setting data anyway for user:', user.uid);
            }

            // Combine owned and collaborative, removing duplicates
            const allLists = [...ownedLists];
            collaborativeLists.forEach(collabList => {
              const isDuplicate = ownedLists.some(owned => owned.id === collabList.id);
              if (!isDuplicate) {
                allLists.push(collabList);
              }
            });

            // CRITICAL FIX: Convert watchlists WITHOUT profile fetching first
            // Profile fetching (getCachedUserProfile, resolveCollaboratorProfiles) can hang
            // indefinitely on native, blocking setUserWatchlists from ever being called.
            // Strategy: Set watchlist data immediately, then enrich with profiles in background.
            const quickConvertedLists: Watchlist[] = allLists.map((list) => {
              let movieDetails: Movie[] = [];
              if (list.movieDetails && Array.isArray(list.movieDetails)) {
                movieDetails = list.movieDetails
                  .filter((movie: any) => movie && (movie.id || movie.imdbID) && (movie.title || movie.Title))
                  .map((movie: any) => ({
                    ...movie,
                    Title: movie.Title || movie.title || 'Unknown Movie',
                    title: movie.title || movie.Title || 'Unknown Movie',
                    Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                    year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                    Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                    imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                    id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                  }));
              }

              const isOwnedList = list.createdBy === user.uid;

              return {
                id: list.id,
                name: list.name,
                description: list.description || '',
                movieDetails,
                isPrivate: list.isPrivate,
                createdBy: list.createdBy,
                createdByName: isOwnedList ? (user.displayName || 'You') : (list.createdBy || 'Unknown'),
                createdByAvatar: undefined,
                createdAt: list.createdAt,
                updatedAt: list.updatedAt,
                collaborators: list.collaborators || [],
                adminCollaboratorIds: list.adminCollaboratorIds,
                collaboratorNames: [],
                collaboratorProfiles: [],
                followerCount: list.followerCount || 0,
                tags: [],
                isFollowing: false,
                isCollaborative: !isOwnedList
              } as Watchlist;
            });

            if (isCancelled) {
              // Data converted but effect was cancelled - still commit to avoid lost work
              console.log('UserWatchlistDataContext: Effect cancelled after processing, committing data anyway');
            }

            // IMMEDIATELY set watchlist data so UI renders right away
            console.log('UserWatchlistDataContext: Native - setting', quickConvertedLists.length, 'user watchlists (quick, no profiles)');
            setUserWatchlists(quickConvertedLists);
            updateStats();
            // Set loading to false IMMEDIATELY so sections show content
            dataLoadedForUserRef.current = user.uid;
            isLoadingRef.current = false;
            setIsLoading(false);

            // NOW enrich with profiles in background (non-blocking)
            // If this hangs or fails, the UI already has the data
            (async () => {
              if (isCancelled) return;
              try {
                const enrichedLists: Watchlist[] = await withTimeout(
                  Promise.all(
                    quickConvertedLists.map(async (list) => {
                      const isOwnedList = list.createdBy === user.uid;
                      let createdByName = list.createdByName;
                      let createdByAvatar: string | undefined = undefined;

                      if (!isOwnedList) {
                        try {
                          const creatorProfile = await withTimeout(
                            getCachedUserProfile(list.createdBy),
                            3000,
                            null,
                            `profile-${list.createdBy}`
                          );
                          if (creatorProfile) {
                            createdByName = creatorProfile.displayName || 'Unknown User';
                            createdByAvatar = creatorProfile.photoURL;
                          }
                        } catch (e) { /* ignore */ }
                      }

                      const rawProfiles = await withTimeout(
                        resolveCollaboratorProfiles(list.collaborators || []),
                        3000,
                        [] as CollaboratorProfile[],
                        `collaborators-${list.id}`
                      );
                      const collaboratorProfiles = applyCollaboratorAdminRoles(
                        rawProfiles,
                        list.adminCollaboratorIds
                      );

                      return {
                        ...list,
                        createdByName,
                        createdByAvatar,
                        collaboratorNames: collaboratorProfiles.map(p => p.name),
                        collaboratorProfiles,
                      };
                    })
                  ),
                  8000,
                  quickConvertedLists, // Fallback to original if entire enrichment times out
                  'profile-enrichment-all'
                );

                if (!isCancelled) {
                  console.log('UserWatchlistDataContext: Native - enriched watchlists with profiles');
                  setUserWatchlists(enrichedLists);
                }
              } catch (e) {
                console.warn('UserWatchlistDataContext: Native - profile enrichment failed, keeping original data:', e);
              }
            })();

            // Load liked watchlists in background (non-blocking for main loading state)
            (async () => {
              if (isCancelled) return;
              try {
                const likedLists = await withTimeout(
                  getCachedLikedWatchlists(user.uid),
                  10000,
                  [],
                  'getCachedLikedWatchlists'
                );
                console.log('UserWatchlistDataContext: Native - got', likedLists.length, 'liked watchlists');

                if (isCancelled) return;

                const convertedLikedLists: Watchlist[] = likedLists.map(list => {
                  let movieDetails: Movie[] = [];
                  if (list.movieDetails && Array.isArray(list.movieDetails)) {
                    movieDetails = list.movieDetails
                      .filter((movie: any) => movie && (movie.id || movie.imdbID) && (movie.title || movie.Title))
                      .map((movie: any) => ({
                        ...movie,
                        Title: movie.Title || movie.title || 'Unknown Movie',
                        title: movie.title || movie.Title || 'Unknown Movie',
                        Year: movie.Year || movie.year || 'Unknown',
                        year: movie.year || movie.Year || 'Unknown',
                        Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                        imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                        id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                      }));
                  }

                  return {
                    id: list.id,
                    name: list.name,
                    description: list.description || '',
                    movieDetails,
                    isPrivate: list.isPrivate,
                    createdBy: list.createdBy,
                    createdByName: list.createdBy,
                    createdByAvatar: undefined,
                    createdAt: list.createdAt,
                    updatedAt: list.updatedAt,
                    collaborators: list.collaborators || [],
                    collaboratorNames: [],
                    followerCount: list.followerCount || 0,
                    tags: [],
                    isFollowing: true
                  };
                });

                if (!isCancelled) {
                  console.log('UserWatchlistDataContext: Native - setting', convertedLikedLists.length, 'liked watchlists');
                  setLikedWatchlists(convertedLikedLists);
                  setLikedWatchlistsReady(true);
                }

                // Load public watchlists
                const publicLists = await withTimeout(
                  watchlistService.getPublicWatchlists(50),
                  10000,
                  [],
                  'getPublicWatchlists'
                );
                const likedIds = new Set(likedLists.map(l => l.id));
                const convertedPublicLists = publicLists
                  .filter(list => list.createdBy !== user.uid)
                  .map(list => ({
                    id: list.id,
                    name: list.name,
                    description: list.description || '',
                    movieDetails: list.movieDetails || [],
                    isPrivate: false,
                    createdBy: list.createdBy,
                    createdByName: list.createdBy,
                    createdAt: list.createdAt,
                    updatedAt: list.updatedAt,
                    collaborators: [],
                    collaboratorNames: [],
                    followerCount: list.followerCount || 0,
                    tags: [],
                    isFollowing: likedIds.has(list.id)
                  }));

                if (!isCancelled) {
                  setAllWatchlists(convertedPublicLists as Watchlist[]);
                  console.log('✅ UserWatchlistDataContext: Native liked+public data load complete');
                }
              } catch (error) {
                console.warn('UserWatchlistDataContext: Native - liked/public watchlists load error (non-fatal):', error);
                setLikedWatchlistsReady(true);
              }
            })();

          } catch (error) {
            console.error('UserWatchlistDataContext: Native query error:', error);
            isLoadingRef.current = false;
            setIsLoading(false);
            setLikedWatchlistsReady(true);
          }

          return; // Skip web subscription setup for native
        }

        console.log('UserWatchlistDataContext: Setting up Firestore listeners for user:', user.uid);

        // Subscribe to owned watchlists with error handling
        // CRITICAL: Firestore subscriptions fail on Android with permission errors
        // We need to handle this and fallback to regular queries
        let subscriptionFailed = false;

        try {
          unsubscribeOwned = watchlistService.subscribeToUserWatchlists(user.uid, async (firebaseWatchlists) => {
            if (isCancelled || subscriptionFailed) return;

            console.log('UserWatchlistDataContext: Received owned watchlists update:', firebaseWatchlists.length, 'lists');

            try {
              // Convert Firebase watchlists to our format - minimize collaborator profile calls
              const convertedLists: Watchlist[] = await Promise.all(
                firebaseWatchlists.map(async (list) => {
                  let movieDetails: Movie[] = [];

                  if (list.movieDetails && Array.isArray(list.movieDetails)) {
                    movieDetails = list.movieDetails
                      .filter((movie: any) => movie && (movie.id || movie.imdbID) && (movie.title || movie.Title))
                      .map((movie: any) => ({
                        ...movie,
                        Title: movie.Title || movie.title || 'Unknown Movie',
                        title: movie.title || movie.Title || 'Unknown Movie',
                        Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                        year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                        Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                        poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
                        imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                        id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                      }));
                  }

                  // Only resolve collaborator profiles on initial load to avoid excessive API calls
                  const collaboratorProfiles = isInitialLoad
                    ? applyCollaboratorAdminRoles(
                        await resolveCollaboratorProfiles(list.collaborators || []),
                        list.adminCollaboratorIds
                      )
                    : [];

                  return {
                    id: list.id,
                    name: list.name,
                    description: list.description || '',
                    movieDetails,
                    isPrivate: list.isPrivate,
                    createdBy: list.createdBy,
                    createdByName: user.displayName || 'You',
                    createdAt: list.createdAt,
                    updatedAt: list.updatedAt,
                    collaborators: list.collaborators || [],
                    adminCollaboratorIds: list.adminCollaboratorIds,
                    collaboratorNames: collaboratorProfiles.map(p => p.name),
                    collaboratorProfiles,
                    followerCount: list.followerCount || 0,
                    tags: [],
                    isFollowing: false
                  } as Watchlist;
                })
              );

              ownedWatchlists = convertedLists;
              mergeWatchlists();

              // Load liked watchlists on initial load (check BEFORE setting isInitialLoad to false)
              console.log('📋 UserWatchlistDataContext: About to check isInitialLoad...', { isInitialLoad, isCancelled });
              if (isInitialLoad && !isCancelled) {
                console.log('📋 UserWatchlistDataContext: Loading liked watchlists after owned watchlists loaded');
                try {
                  const likedLists = await getCachedLikedWatchlists(user.uid);
                  console.log('✅ UserWatchlistDataContext: Got', likedLists.length, 'liked watchlists');

                  // OPTIMIZATION: Batch preload creator profiles before mapping
                  const creatorIds = likedLists.map(list => list.createdBy).filter(id => id);
                  if (creatorIds.length > 0) {
                    await batchFetchProfiles([...new Set(creatorIds)]);
                  }

                  const convertedLikedLists: Watchlist[] = await Promise.all(
                    likedLists.map(async list => {
                      let createdByName = list.createdBy;
                      let createdByAvatar: string | undefined = undefined;

                      try {
                        const creatorProfile = await getCachedUserProfile(list.createdBy);
                        if (creatorProfile) {
                          createdByName = creatorProfile.displayName || creatorProfile.email?.split('@')[0] || 'Unknown User';
                          createdByAvatar = creatorProfile.photoURL;
                        }
                      } catch (error) {
                        // Silently handle profile fetch errors
                      }

                      let movieDetails: Movie[] = [];
                      if (list.movieDetails && Array.isArray(list.movieDetails)) {
                        movieDetails = list.movieDetails
                          .filter((movie: any) => movie && (movie.id || movie.imdbID) && (movie.title || movie.Title))
                          .map((movie: any) => ({
                            ...movie,
                            Title: movie.Title || movie.title || 'Unknown Movie',
                            title: movie.title || movie.Title || 'Unknown Movie',
                            Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                            year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                            Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                            poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
                            imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                            id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                          }));
                      }

                      return {
                        id: list.id,
                        name: list.name,
                        description: list.description || '',
                        movieDetails,
                        isPrivate: list.isPrivate,
                        createdBy: list.createdBy,
                        createdByName,
                        createdByAvatar,
                        createdAt: list.createdAt,
                        updatedAt: list.updatedAt,
                        collaborators: list.collaborators || [],
                        collaboratorNames: list.collaborators || [],
                        followerCount: list.followerCount || 0,
                        tags: [],
                        isFollowing: true
                      };
                    })
                  );

                  console.log('✅ UserWatchlistDataContext: Setting', convertedLikedLists.length, 'liked watchlists');
                  setLikedWatchlists(convertedLikedLists);

                  // Load public watchlists for discovery/trending (inside same try block to access convertedLikedLists)
                  console.log('📥 UserWatchlistDataContext: Fetching public watchlists...');
                  const publicLists = await watchlistService.getPublicWatchlists(50);
                  console.log('✅ UserWatchlistDataContext: Got public watchlists from service:', publicLists.length);

                  // OPTIMIZATION: Batch preload public watchlist creator profiles
                  const publicCreatorIds = publicLists
                    .filter(list => list.createdBy !== user.uid)
                    .map(list => list.createdBy)
                    .filter(id => id);
                  if (publicCreatorIds.length > 0) {
                    await batchFetchProfiles([...new Set(publicCreatorIds)]);
                  }

                  const likedIds = new Set(convertedLikedLists.map(list => list.id));
                  const convertedPublicLists: Watchlist[] = await Promise.all(
                    publicLists
                      .filter(list => list.createdBy !== user.uid)
                      .map(async (list) => {
                        let createdByName = list.createdBy;
                        let createdByAvatar: string | undefined = undefined;

                        try {
                          const creatorProfile = await getCachedUserProfile(list.createdBy);
                          if (creatorProfile) {
                            createdByName = creatorProfile.displayName || creatorProfile.email?.split('@')[0] || 'Unknown User';
                            createdByAvatar = creatorProfile.photoURL;
                          }
                        } catch (error) {
                          // Silently handle profile fetch errors
                        }

                        let movieDetails: Movie[] = [];
                        if (list.movieDetails && Array.isArray(list.movieDetails)) {
                          movieDetails = list.movieDetails
                            .filter((movie: any) => movie && (movie.id || movie.imdbID) && (movie.title || movie.Title))
                            .map((movie: any) => ({
                              ...movie,
                              Title: movie.Title || movie.title || 'Unknown Movie',
                              title: movie.title || movie.Title || 'Unknown Movie',
                              Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                              year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                              Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                              poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
                              imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                              id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                            }));
                        }

                        return {
                          id: list.id,
                          name: list.name,
                          description: list.description || '',
                          movieDetails,
                          isPrivate: list.isPrivate,
                          createdBy: list.createdBy,
                          createdByName,
                          createdByAvatar,
                          createdAt: list.createdAt,
                          updatedAt: list.updatedAt,
                          collaborators: list.collaborators || [],
                          collaboratorNames: list.collaborators || [],
                          followerCount: list.followerCount || 0,
                          tags: [],
                          isFollowing: likedIds.has(list.id)
                        };
                      })
                  );

                  console.log('✅ UserWatchlistDataContext: Setting all watchlists (public):', convertedPublicLists.length);
                  setAllWatchlists(convertedPublicLists);
                } catch (error) {
                  console.error('UserWatchlistDataContext: Error loading liked watchlists:', error);
                  setLikedWatchlists([]);
                  setAllWatchlists([]);
                } finally {
                  setLikedWatchlistsReady(true);
                }

                // Disable expensive operations after first load (moved here after liked watchlists are loaded)
                isInitialLoad = false;
                setIsLoading(false);
              }

            } catch (error) {
              console.error('UserWatchlistDataContext: Error processing owned watchlists real-time update:', error);
            }
          });

          // Add a timeout to detect if subscription never fires
          const subscriptionTimeout = setTimeout(async () => {
            if (isInitialLoad && !isCancelled) {
              console.warn('UserWatchlistDataContext: Subscription timeout - falling back to direct query');
              subscriptionFailed = true;

              try {
                // 🚀 MAJOR FIX: Load BOTH owned AND collaborative watchlists in fallback
                // This fixes the issue where collaborative watchlists don't appear for collaborators
                const [ownedLists, collaborativeLists] = await Promise.all([
                  watchlistService.getUserWatchlists(user.uid),
                  watchlistService.getCollaborativeWatchlists(user.uid)
                ]);

                console.log('UserWatchlistDataContext: Fallback query got', ownedLists.length, 'owned watchlists');
                console.log('🤝 UserWatchlistDataContext: Fallback query got', collaborativeLists.length, 'collaborative watchlists');

                // Combine owned and collaborative, removing duplicates
                const allLists = [...ownedLists];
                collaborativeLists.forEach(collabList => {
                  const isDuplicate = ownedLists.some(owned => owned.id === collabList.id);
                  if (!isDuplicate) {
                    allLists.push(collabList);
                  }
                });

                console.log('✅ UserWatchlistDataContext: Fallback total:', allLists.length, 'watchlists (owned + collaborative)');

                const convertedLists: Watchlist[] = await Promise.all(
                  allLists.map(async (list) => {
                    let movieDetails: Movie[] = [];

                    if (list.movieDetails && Array.isArray(list.movieDetails)) {
                      movieDetails = list.movieDetails
                        .filter((movie: any) => movie && (movie.id || movie.imdbID) && (movie.title || movie.Title))
                        .map((movie: any) => ({
                          ...movie,
                          Title: movie.Title || movie.title || 'Unknown Movie',
                          title: movie.title || movie.Title || 'Unknown Movie',
                          Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                          year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                          Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                          poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
                          imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                          id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                        }));
                    }

                    const collaboratorProfiles = applyCollaboratorAdminRoles(
                      await resolveCollaboratorProfiles(list.collaborators || []),
                      list.adminCollaboratorIds
                    );

                    // Determine if this list is owned or collaborative
                    const isOwnedList = list.createdBy === user.uid;

                    // Get creator info for collaborative watchlists
                    let createdByName = user.displayName || 'You';
                    let createdByAvatar: string | undefined = undefined;

                    if (!isOwnedList) {
                      try {
                        const creatorProfile = await getCachedUserProfile(list.createdBy);
                        if (creatorProfile) {
                          createdByName = creatorProfile.displayName || creatorProfile.email?.split('@')[0] || 'Unknown User';
                          createdByAvatar = creatorProfile.photoURL;
                        }
                      } catch (error) {
                        console.warn('Failed to fetch creator profile for collaborative list:', list.createdBy);
                      }
                    }

                    return {
                      id: list.id,
                      name: list.name,
                      description: list.description || '',
                      movieDetails,
                      isPrivate: list.isPrivate,
                      createdBy: list.createdBy,
                      createdByName,
                      createdByAvatar,
                      createdAt: list.createdAt,
                      updatedAt: list.updatedAt,
                      collaborators: list.collaborators || [],
                      adminCollaboratorIds: list.adminCollaboratorIds,
                      collaboratorNames: collaboratorProfiles.map(p => p.name),
                      collaboratorProfiles,
                      followerCount: list.followerCount || 0,
                      tags: [],
                      isFollowing: false,
                      isCollaborative: !isOwnedList // Mark collaborative lists
                    } as Watchlist;
                  })
                );

                ownedWatchlists = convertedLists;
                collaborativeWatchlistsData = convertedLists.filter(list => list.createdBy !== user.uid);
                mergeWatchlists();

                // CRITICAL FIX: Also load liked watchlists in the fallback path!
                // On native platforms, the real-time subscription may not fire,
                // so we need to load liked watchlists here as well
                console.log('📋 UserWatchlistDataContext: Fallback path - loading liked watchlists...');
                try {
                  const likedLists = await getCachedLikedWatchlists(user.uid);
                  console.log('✅ UserWatchlistDataContext: Fallback got', likedLists.length, 'liked watchlists');

                  const convertedLikedLists: Watchlist[] = await Promise.all(
                    likedLists.map(async list => {
                      let createdByName = list.createdBy;
                      let createdByAvatar: string | undefined = undefined;

                      try {
                        const creatorProfile = await getCachedUserProfile(list.createdBy);
                        if (creatorProfile) {
                          createdByName = creatorProfile.displayName || creatorProfile.email?.split('@')[0] || 'Unknown User';
                          createdByAvatar = creatorProfile.photoURL;
                        }
                      } catch (error) {
                        // Silently handle profile fetch errors
                      }

                      let movieDetails: Movie[] = [];
                      if (list.movieDetails && Array.isArray(list.movieDetails)) {
                        movieDetails = list.movieDetails
                          .filter((movie: any) => movie && (movie.id || movie.imdbID) && (movie.title || movie.Title))
                          .map((movie: any) => ({
                            ...movie,
                            Title: movie.Title || movie.title || 'Unknown Movie',
                            title: movie.title || movie.Title || 'Unknown Movie',
                            Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                            year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                            Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                            poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
                            imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                            id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                          }));
                      }

                      return {
                        id: list.id,
                        name: list.name,
                        description: list.description || '',
                        movieDetails,
                        isPrivate: list.isPrivate,
                        createdBy: list.createdBy,
                        createdByName,
                        createdByAvatar,
                        createdAt: list.createdAt,
                        updatedAt: list.updatedAt,
                        collaborators: list.collaborators || [],
                        collaboratorNames: list.collaborators || [],
                        followerCount: list.followerCount || 0,
                        tags: [],
                        isFollowing: true
                      };
                    })
                  );

                  console.log('✅ UserWatchlistDataContext: Fallback - Setting', convertedLikedLists.length, 'liked watchlists');
                  setLikedWatchlists(convertedLikedLists);
                } catch (likedError) {
                  console.error('UserWatchlistDataContext: Error loading liked watchlists in fallback:', likedError);
                  setLikedWatchlists([]);
                } finally {
                  setLikedWatchlistsReady(true);
                }
              } catch (fallbackError) {
                console.error('UserWatchlistDataContext: Fallback query also failed:', fallbackError);
              } finally {
                isInitialLoad = false;
                setIsLoading(false);
                setLikedWatchlistsReady(true);
              }
            }
          }, 2000); // Wait 2 seconds for subscription to fire

          // NOTE: Don't return early here! We need to continue to set up collaborative watchlists subscription below
        } catch (error) {
          console.error('UserWatchlistDataContext: Error setting up owned watchlists subscription:', error);
          subscriptionFailed = true;

          // Immediately fallback to direct query if subscription setup fails
          try {
            const lists = await watchlistService.getUserWatchlists(user.uid);
            console.log('UserWatchlistDataContext: Setup failed, direct query got', lists.length, 'watchlists');
            // Process lists same way as above...
            ownedWatchlists = lists as any;
            mergeWatchlists();
          } catch (fallbackError) {
            console.error('UserWatchlistDataContext: Fallback query failed:', fallbackError);
          }
        }

        // Subscribe to collaborative watchlists with error handling
        console.log('🤝 UserWatchlistDataContext: Setting up collaborative watchlists subscription for user:', user.uid);
        try {
          unsubscribeCollaborative = watchlistService.subscribeToCollaborativeWatchlists(user.uid, async (firebaseWatchlists) => {
            console.log('🤝 UserWatchlistDataContext: Received collaborative watchlists update:', firebaseWatchlists.length, 'lists');

            try {
              // Convert Firebase watchlists to our format
              const convertedLists: Watchlist[] = await Promise.all(
                firebaseWatchlists.map(async (list) => {
                  let movieDetails: Movie[] = [];

                  if (list.movieDetails && Array.isArray(list.movieDetails)) {
                    movieDetails = list.movieDetails
                      .filter((movie: any) => movie && (movie.id || movie.imdbID) && (movie.title || movie.Title))
                      .map((movie: any) => ({
                        ...movie,
                        Title: movie.Title || movie.title || 'Unknown Movie',
                        title: movie.title || movie.Title || 'Unknown Movie',
                        Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                        year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                        Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                        poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
                        imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                        id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                      }));
                  }

                  // Try to get actual user profile for creator info
                  let createdByName = list.createdBy;
                  let createdByAvatar: string | undefined = undefined;

                  try {
                    const creatorProfile = await getCachedUserProfile(list.createdBy);
                    if (creatorProfile) {
                      createdByName = creatorProfile.displayName || creatorProfile.email?.split('@')[0] || 'Unknown User';
                      createdByAvatar = creatorProfile.photoURL;
                    }
                  } catch (error) {
                    console.warn('Failed to fetch creator profile for:', list.createdBy);
                  }

                  // Resolve collaborator profiles
                  const collaboratorProfiles = applyCollaboratorAdminRoles(
                    await resolveCollaboratorProfiles(list.collaborators || []),
                    list.adminCollaboratorIds
                  );

                  return {
                    id: list.id,
                    name: list.name,
                    description: list.description || '',
                    movieDetails,
                    isPrivate: list.isPrivate,
                    createdBy: list.createdBy,
                    createdByName,
                    createdByAvatar,
                    createdAt: list.createdAt,
                    updatedAt: list.updatedAt,
                    collaborators: list.collaborators || [],
                    adminCollaboratorIds: list.adminCollaboratorIds,
                    collaboratorNames: collaboratorProfiles.map(p => p.name),
                    collaboratorProfiles,
                    followerCount: list.followerCount || 0,
                    tags: [],
                    isFollowing: false
                  } as Watchlist;
                })
              );

              collaborativeWatchlistsData = convertedLists;
              mergeWatchlists();

            } catch (error) {
              console.error('UserWatchlistDataContext: Error processing collaborative watchlists real-time update:', error);
            }
          });
        } catch (error) {
          console.error('UserWatchlistDataContext: Error setting up collaborative watchlists subscription:', error);
        }

        // Load additional data (liked watchlists, public watchlists) without triggering watchlist refresh
        const loadAdditionalData = async () => {
          try {
            console.log('🔍 UserWatchlistDataContext: loadAdditionalData starting', {
              isFirebaseInitialized: isFirebaseInitialized(),
              userId: user.uid
            });

            if (!isFirebaseInitialized()) {
              console.warn('⚠️ UserWatchlistDataContext: Firebase not initialized, skipping additional data load');
              return;
            }

            // Loading additional data silently to reduce console spam

            // Load liked watchlists (optional - gracefully handle permission errors)
            // Using cache to prevent redundant fetches
            let likedLists: any[] = [];
            try {
              console.log('📋 UserWatchlistDataContext: Fetching liked watchlists...');
              likedLists = await getCachedLikedWatchlists(user.uid);
              console.log('✅ UserWatchlistDataContext: Got liked watchlists:', likedLists.length);
            } catch (likedError) {
              // Silently handle permission errors for liked watchlists
              const isPermissionError = likedError instanceof Error && likedError.message.includes('permission');
              if (!isPermissionError) {
                console.warn('UserWatchlistDataContext: Could not load liked watchlists:', likedError);
              } else {
                console.warn('⚠️ UserWatchlistDataContext: Permission error loading liked watchlists');
              }
            }
            const convertedLikedLists: Watchlist[] = await Promise.all(
              likedLists.map(async list => {
                let createdByName = list.createdBy;
                let createdByAvatar: string | undefined = undefined;

                try {
                  const creatorProfile = await getCachedUserProfile(list.createdBy);
                  if (creatorProfile) {
                    createdByName = creatorProfile.displayName || creatorProfile.email?.split('@')[0] || 'Unknown User';
                    createdByAvatar = creatorProfile.photoURL;
                  }
                } catch (error) {
                  // Silently handle profile fetch errors
                }

                let movieDetails: Movie[] = [];
                if (list.movieDetails && Array.isArray(list.movieDetails)) {
                  movieDetails = list.movieDetails
                    .filter((movie: any) => movie && (movie.id || movie.imdbID) && (movie.title || movie.Title))
                    .map((movie: any) => ({
                      ...movie,
                      Title: movie.Title || movie.title || 'Unknown Movie',
                      title: movie.title || movie.Title || 'Unknown Movie',
                      Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                      year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                      Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                      poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
                      imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                      id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                    }));
                }

                return {
                  id: list.id,
                  name: list.name,
                  description: list.description || '',
                  movieDetails,
                  isPrivate: list.isPrivate,
                  createdBy: list.createdBy,
                  createdByName,
                  createdByAvatar,
                  createdAt: list.createdAt,
                  updatedAt: list.updatedAt,
                  collaborators: list.collaborators || [],
                  collaboratorNames: list.collaborators || [],
                  followerCount: list.followerCount || 0,
                  tags: [],
                  isFollowing: true
                };
              })
            );

            // Load public watchlists (optional - gracefully handle permission errors)
            let publicLists: any[] = [];
            try {
              console.log('📥 UserWatchlistDataContext: Fetching public watchlists...');
              publicLists = await watchlistService.getPublicWatchlists(50);
              console.log('✅ UserWatchlistDataContext: Got public watchlists from service:', publicLists.length);
              publicLists.forEach((list, idx) => {
                console.log(`  Raw ${idx + 1}. ${list.name} (isPrivate: ${list.isPrivate}, createdBy: ${list.createdBy})`);
              });
            } catch (publicError) {
              // Silently handle permission errors for public watchlists
              const isPermissionError = publicError instanceof Error && publicError.message.includes('permission');
              if (!isPermissionError) {
                console.warn('UserWatchlistDataContext: Could not load public watchlists:', publicError);
              } else {
                console.warn('UserWatchlistDataContext: Permission error loading public watchlists');
              }
            }

            const likedIds = new Set(likedLists.map(list => list.id));
            console.log('🔄 UserWatchlistDataContext: Converting public watchlists...', {
              publicListsCount: publicLists.length,
              afterFilteringOwnLists: publicLists.filter(list => list.createdBy !== user.uid).length
            });
            const convertedPublicLists: Watchlist[] = await Promise.all(
              publicLists
                .filter(list => list.createdBy !== user.uid)
                .map(async (list, idx) => {
                  console.log(`🔄 Converting watchlist ${idx + 1}/${publicLists.length}: ${list.name}`);
                  try {
                    let createdByName = list.createdBy;
                    let createdByAvatar: string | undefined = undefined;

                    try {
                      const creatorProfile = await getCachedUserProfile(list.createdBy);
                      if (creatorProfile) {
                        createdByName = creatorProfile.displayName || creatorProfile.email?.split('@')[0] || 'Unknown User';
                        createdByAvatar = creatorProfile.photoURL;
                      }
                    } catch (error) {
                      // Silently handle profile fetch errors
                    }

                    let movieDetails: Movie[] = [];
                    if (list.movieDetails && Array.isArray(list.movieDetails)) {
                      movieDetails = list.movieDetails
                        .filter((movie: any) => movie && (movie.id || movie.imdbID) && (movie.title || movie.Title))
                        .map((movie: any) => ({
                          ...movie,
                          Title: movie.Title || movie.title || 'Unknown Movie',
                          title: movie.title || movie.Title || 'Unknown Movie',
                          Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
                          year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
                          Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
                          poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
                          imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
                          id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
                        }));
                    }

                    return {
                      id: list.id,
                      name: list.name,
                      description: list.description || '',
                      movieDetails,
                      isPrivate: list.isPrivate,
                      createdBy: list.createdBy,
                      createdByName,
                      createdByAvatar,
                      createdAt: list.createdAt,
                      updatedAt: list.updatedAt,
                      collaborators: list.collaborators || [],
                      collaboratorNames: list.collaborators || [],
                      followerCount: list.followerCount || 0,
                      tags: [],
                      isFollowing: likedIds.has(list.id)
                    };
                  } catch (conversionError) {
                    console.error(`❌ Error converting watchlist ${list.name}:`, conversionError);
                    // Return a minimal watchlist object to avoid breaking the Promise.all
                    return {
                      id: list.id,
                      name: list.name,
                      description: list.description || '',
                      movieDetails: [],
                      isPrivate: list.isPrivate,
                      createdBy: list.createdBy,
                      createdByName: list.createdBy,
                      createdAt: list.createdAt,
                      updatedAt: list.updatedAt,
                      collaborators: [],
                      collaboratorNames: [],
                      followerCount: 0,
                      tags: [],
                      isFollowing: false
                    };
                  }
                })
            );

            console.log('✅ UserWatchlistDataContext: Setting liked watchlists:', convertedLikedLists.length);
            console.log('✅ UserWatchlistDataContext: Setting all watchlists (public):', convertedPublicLists.length);
            convertedPublicLists.forEach((list, idx) => {
              console.log(`  ${idx + 1}. ${list.name} (${list.movieDetails.length} movies, private: ${list.isPrivate}, createdBy: ${list.createdBy})`);
            });
            setLikedWatchlists(convertedLikedLists);
            setLikedWatchlistsReady(true);
            setAllWatchlists(convertedPublicLists);
          } catch (error) {
            // Check if it's a permission error (Firestore security rules not configured)
            const isPermissionError = error instanceof Error && error.message.includes('permission');

            if (isPermissionError) {
              console.log('UserWatchlistDataContext: Some social features disabled (Firestore permissions not configured)');
            } else {
              console.error('UserWatchlistDataContext: Error loading additional data:', error);
            }

            // Set empty arrays for optional social features
            console.warn('⚠️ UserWatchlistDataContext: Setting liked watchlists to empty due to error');
            setLikedWatchlists([]);
            setLikedWatchlistsReady(true);
            setAllWatchlists([]);
          }
        };

        console.log('🚀 UserWatchlistDataContext: About to call loadAdditionalData()');
        loadAdditionalData().finally(() => {
          // CRITICAL FIX: Ensure loading state is set to false after loadAdditionalData completes
          // This handles the case where subscription callbacks don't fire on iOS native
          // or when isCancelled becomes true due to effect re-runs
          if (!isCancelled) {
            console.log('✅ UserWatchlistDataContext: loadAdditionalData complete, ensuring loading is false');
            setIsLoading(false);
            setLikedWatchlistsReady(true);
          }
        });
      };

      // Call the async initializer
      initializeListeners();

      // Return cleanup function
      return () => {
        isCancelled = true;
        // NOTE: Do NOT set isLoading to false in cleanup - this causes flash of empty content
        // when the effect re-runs. The native queries will set loading to false when they complete.
        // Only unsubscribe from any active subscriptions (web platform only)
        console.log('🧹 UserWatchlistDataContext: Effect cleanup - cancelling pending operations');
        if (unsubscribeOwned) unsubscribeOwned();
        if (unsubscribeCollaborative) unsubscribeCollaborative();
      };
    } else if (!user) {
      // Reset refs when user logs out
      dataLoadedForUserRef.current = null;
      isLoadingRef.current = false;
      // Load from localStorage for non-authenticated users
      loadLocalData();
    } else if (user && !isFirebaseReady) {
      // User is authenticated but Firebase not ready yet (common on Android)
      console.log('UserWatchlistDataContext: User authenticated but Firebase not ready yet, waiting...');
    }
    // CRITICAL: Use user?.uid instead of user as dependency.
    // On native, AuthContext creates a NEW user object on every auth state change,
    // even for the same user. Using the full `user` object causes this effect to re-run,
    // which triggers cleanup (isCancelled = true) and aborts in-progress data loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, isFirebaseReady, loadLocalData]);

  // Save to localStorage whenever userWatchlists or likedWatchlists change
  useEffect(() => {
    if (!user && userWatchlists.length > 0) {
      saveLocalData();
    }
  }, [userWatchlists, likedWatchlists, user, saveLocalData]);

  // Removed auto-sync useEffect to prevent infinite loops
  // The auto-sync functionality was causing continuous refreshing
  // TODO: Implement a more stable auto-sync mechanism if needed

  // Add debugging functions to window object
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).debugWatchlists = {
        getUserWatchlists: () => {
          console.log('DEBUG: Current userWatchlists state:', userWatchlists);
          console.log('DEBUG: Total watchlists:', userWatchlists.length);
          userWatchlists.forEach((list, index) => {
            console.log(`DEBUG: Watchlist ${index + 1} - ${list.name}:`, {
              id: list.id,
              moviesCount: list.movieDetails.length,
              movieDetails: list.movieDetails
            });
          });
          return userWatchlists;
        },
        getFollowedWatchlists: () => likedWatchlists,
        getAllWatchlists: () => allWatchlists,
        refreshData: async () => {
          console.log('DEBUG: Manual refresh triggered');
          await refreshData();
          console.log('DEBUG: Manual refresh completed');
        },
        syncWatchlistData: (watchlistId: string) => syncWatchlistData(watchlistId),
        checkFirebaseData: async () => {
          if (!user) {
            console.log('DEBUG: No user authenticated');
            return;
          }

          // Check if Firebase is properly initialized before attempting operations
          if (!isFirebaseInitialized()) {
            console.warn('DEBUG: Firebase not yet initialized, skipping Firebase data check');
            return;
          }

          console.log('DEBUG: Checking raw Firebase data...');
          const rawUserLists = await watchlistService.getUserWatchlists(user.uid);
          console.log('DEBUG: Raw Firebase watchlists:', rawUserLists);
          rawUserLists.forEach((list, index) => {
            console.log(`DEBUG: Raw Firebase list ${index + 1} - ${list.name}:`, {
              id: list.id,
              movieDetailsArray: list.movieDetails,
              movieDetailsArrayCast: (list as any).movieDetails
            });
          });
        },
        getStats: () => stats
      };
    }
  }, [userWatchlists, likedWatchlists, allWatchlists, user, stats]);

  // Load default watchlist settings on user login
  useEffect(() => {
    const loadSettings = async () => {
      if (user) {
        try {
          const settings = await watchlistService.getDefaultWatchlistSettings(user.uid);
          setDefaultWatchlistSettings(settings || {});
        } catch (err) {
          // Silently handle permission errors - default watchlist settings are optional
          // This is expected if Firestore security rules haven't been configured yet
          console.log('UserWatchlistDataContext: Could not load default watchlist settings (using defaults)');
          setDefaultWatchlistSettings({});
        }
      } else {
        setDefaultWatchlistSettings({});
      }
    };
    loadSettings();
  }, [user]);

  // Listen for social events to update like state in real-time
  useEffect(() => {
    if (!user) return;

    const handleSocialEvent = (eventData: SocialEventData) => {
      // Only handle like/unlike events
      if (eventData.type !== 'like' && eventData.type !== 'unlike') return;

      // Only handle events for this user
      if (eventData.userId !== user.uid) return;

      console.log('🎯 UserWatchlistDataContext: Received social event:', eventData);

      if (eventData.type === 'like' && eventData.watchlistId) {
        // User liked a watchlist - add to liked watchlists if not already there
        const watchlistId = eventData.watchlistId;
        setLikedWatchlists(prev => {
          const existingIndex = prev.findIndex(w => w.id === watchlistId);
          if (existingIndex === -1) {
            // Find the watchlist in allWatchlists to add it to liked
            const watchlist = allWatchlists.find(w => w.id === watchlistId);
            if (watchlist) {
              return [...prev, { ...watchlist, followerCount: (watchlist.followerCount || 0) + 1 }];
            }
          }
          return prev;
        });

        // Update like count in all watchlist arrays
        const updateLikeCount = (watchlists: Watchlist[]) =>
          watchlists.map(watchlist =>
            watchlist.id === watchlistId
              ? { ...watchlist, followerCount: (watchlist.followerCount || 0) + 1 }
              : watchlist
          );

        setUserWatchlists(updateLikeCount);
        setAllWatchlists(updateLikeCount);

      } else if (eventData.type === 'unlike' && eventData.watchlistId) {
        // User unliked a watchlist - remove from liked watchlists
        const watchlistId = eventData.watchlistId;
        setLikedWatchlists(prev => prev.filter(w => w.id !== watchlistId));

        // Update like count in all watchlist arrays
        const updateLikeCount = (watchlists: Watchlist[]) =>
          watchlists.map(watchlist =>
            watchlist.id === watchlistId
              ? { ...watchlist, followerCount: Math.max(0, (watchlist.followerCount || 0) - 1) }
              : watchlist
          );

        setUserWatchlists(updateLikeCount);
        setAllWatchlists(updateLikeCount);
      }
    };

    // Subscribe to like/unlike events
    const unsubscribeLike = socialEventBroadcaster.subscribe('like', handleSocialEvent);
    const unsubscribeUnlike = socialEventBroadcaster.subscribe('unlike', handleSocialEvent);

    return () => {
      unsubscribeLike();
      unsubscribeUnlike();
    };
  }, [user?.uid, allWatchlists]);

  const updateStats = () => {
    const totalWatchlists = userWatchlists.length;
    const totalMovies = userWatchlists.reduce((total, list) => total + list.movieDetails.length, 0);
    const totalFollowing = likedWatchlists.length;

    // Get recently added movies (last 10 movies across all lists)
    const allMovies = userWatchlists.flatMap(list =>
      list.movieDetails.map(movie => ({ ...movie, listName: list.name }))
    );
    const recentlyAdded = allMovies
      .sort((a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime())
      .slice(0, 10);

    // Calculate top genres
    const genreCounts: { [genre: string]: number } = {};
    allMovies.forEach(movie => {
      if (movie.genre) {
        const genres = movie.genre.split(',').map(g => g.trim());
        genres.forEach(genre => {
          genreCounts[genre] = (genreCounts[genre] || 0) + 1;
        });
      }
    });

    const topGenres = Object.entries(genreCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([genre]) => genre);

    setStats({
      totalWatchlists,
      totalMovies,
      totalFollowing,
      totalFollowers: 0, // This would come from Firebase in a real implementation
      recentlyAdded,
      topGenres
    });
  };

  const createWatchlist = async (watchlistData: Omit<Watchlist, 'id' | 'createdAt' | 'updatedAt' | 'followerCount'>, shareToFeed: boolean = true): Promise<string> => {
    console.log('UserWatchlistDataContext: createWatchlist called with:', watchlistData);

    if (!user) {
      console.log('UserWatchlistDataContext: No user, creating local watchlist');
      // For non-authenticated users, create locally
      const newWatchlist: Watchlist = {
        ...watchlistData,
        id: `local_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        followerCount: 0
      };

      setUserWatchlists(prev => [...prev, newWatchlist]);
      updateStats();

      // Real-time listener will automatically update the UI when Firebase data changes

      return newWatchlist.id;
    }

    // For authenticated users, persist to Firebase
    try {
      console.log('UserWatchlistDataContext: Creating watchlist in Firebase for user:', user.uid);

      const watchlistId = await watchlistService.saveUserWatchlist(user, {
        name: watchlistData.name,
        description: watchlistData.description,
        isPrivate: watchlistData.isPrivate,
        movieDetails: [], // Start with empty movieDetails array
        collaborators: [] // Always start with empty - collaborators are added via CollaborationRequestService
      });

      console.log('UserWatchlistDataContext: Watchlist created in Firebase with ID:', watchlistId);

      // Optimistic UI: listener / native query can lag; home highlight needs the card in the tree immediately.
      const now = new Date().toISOString();
      const optimisticList: Watchlist = {
        id: watchlistId,
        name: watchlistData.name,
        description: watchlistData.description ?? '',
        movieDetails: [],
        isPrivate: watchlistData.isPrivate,
        createdBy: user.uid,
        createdByName: watchlistData.createdByName || user.displayName || 'You',
        createdAt: now,
        updatedAt: now,
        followerCount: 0,
        collaborators: watchlistData.collaborators ?? [],
      };
      setUserWatchlists((prev) =>
        prev.some((w) => w.id === watchlistId) ? prev : [...prev, optimisticList]
      );
      updateStats();

      // Create activity for watchlist creation (public watchlists only)
      if (!watchlistData.isPrivate) {
        try {
          await socialService.addWatchlistCreatedActivity(
            user.uid,
            watchlistId,
            watchlistData.name,
            watchlistData.movieDetails || [],
            watchlistData.description,
            false,
            !shareToFeed
          );
        } catch (activityError) {
          console.warn('UserWatchlistDataContext: Failed to create watchlist creation activity:', activityError);
          // Don't fail the entire operation if activity creation fails
        }
      }

      // Real-time listener will automatically update the UI when Firebase data changes
      // No need to manually update local state - this was causing duplicate watchlists

      return watchlistId;
    } catch (error) {
      console.error('UserWatchlistDataContext: Error creating watchlist in Firebase:', error);
      setError('Failed to create watchlist');
      throw error;
    }
  };

  const updateWatchlist = async (id: string, updates: Partial<Watchlist>) => {
    // Update local state immediately for optimistic UI
    setUserWatchlists(prev => prev.map(list =>
      list.id === id
        ? { ...list, ...updates, updatedAt: new Date().toISOString() }
        : list
    ));

    // Persist to Firebase if user is authenticated
    if (user) {
      try {
        // Build clean update object without undefined values
        const cleanUpdates: any = {};
        if (updates.name !== undefined) cleanUpdates.name = updates.name;
        if (updates.description !== undefined) cleanUpdates.description = updates.description;
        if (updates.isPrivate !== undefined) cleanUpdates.isPrivate = updates.isPrivate;
        if (updates.collaborators !== undefined) cleanUpdates.collaborators = updates.collaborators;
        if (updates.movieDetails !== undefined) cleanUpdates.movieDetails = updates.movieDetails;

        await watchlistService.updateUserWatchlist(id, cleanUpdates);
        console.log('Watchlist successfully updated in Firebase:', id);

        // If name or description changed, patch the existing watchlist_created activity
        // in place so the feed reflects the new name/description without a new notification.
        if (updates.name !== undefined || updates.description !== undefined) {
          const currentWatchlist = userWatchlists.find(w => w.id === id);
          const isPublic = updates.isPrivate !== undefined ? !updates.isPrivate : !currentWatchlist?.isPrivate;
          if (isPublic) {
            const newName = updates.name ?? currentWatchlist?.name ?? '';
            const newDescription = updates.description ?? currentWatchlist?.description;
            socialService.updateWatchlistActivity(user.uid, id, newName, newDescription).catch(err => {
              console.warn('UserWatchlistDataContext: Failed to patch watchlist activity metadata:', err);
            });
          }
        }
      } catch (error) {
        console.error('Error updating watchlist in Firebase:', error);

        // Revert optimistic update if Firebase operation failed
        setUserWatchlists(prev => prev.map(list =>
          list.id === id
            ? { ...list, updatedAt: new Date().toISOString() } // Revert the update
            : list
        ));

        setError('Failed to update watchlist');
        throw error;
      }
    }

    updateStats();
  };

  const deleteWatchlist = async (id: string) => {
    // Store the watchlist for potential rollback
    const watchlistToDelete = userWatchlists.find(list => list.id === id);

    // Update local state immediately for optimistic UI
    setUserWatchlists(prev => prev.filter(list => list.id !== id));

    // Persist to Firebase if user is authenticated
    if (user) {
      try {
        await watchlistService.deleteUserWatchlist(id);
        console.log('Watchlist successfully deleted from Firebase:', id);
      } catch (error) {
        console.error('Error deleting watchlist from Firebase:', error);

        // Revert optimistic update if Firebase operation failed
        if (watchlistToDelete) {
          setUserWatchlists(prev => [...prev, watchlistToDelete]);
        }

        setError('Failed to delete watchlist');
        throw error;
      }
    }

    updateStats();
  };

  const addMovieToWatchlist = async (watchlistId: string, movie: Omit<Movie, 'addedAt' | 'addedBy'>) => {
    const canonicalId = getStandardizedMovieId(movie);
    const newMovie: Movie = {
      ...movie,
      id: canonicalId,
      addedAt: new Date().toISOString(),
      addedBy: user?.uid || 'anonymous'
    };

    console.log('UserWatchlistDataContext: addMovieToWatchlist called:', {
      watchlistId,
      movieTitle: movie.title,
      movieId: movie.id,
      imdbID: movie.imdbID,
      user: user?.uid
    });

    // Update local state immediately for optimistic UI
    setUserWatchlists(prev => prev.map(list =>
      list.id === watchlistId
        ? {
          ...list,
          movieDetails: [...list.movieDetails, newMovie],
          updatedAt: new Date().toISOString()
        }
        : list
    ));

    // Persist to Firebase if user is authenticated
    if (user) {
      try {
        console.log('UserWatchlistDataContext: Starting Firebase operations for movie:', newMovie.title);

        // TMDB-first canonical id (matches movieCache doc keys when TMDB id exists on the record)
        const movieId = canonicalId;

        // Build movie details object without undefined values
        const movieDetailsForFirebase: any = {
          title: newMovie.title,
          addedBy: newMovie.addedBy,
          id: newMovie.id
        };

        // Only add optional fields if they are defined
        if (newMovie.year !== undefined) movieDetailsForFirebase.year = newMovie.year;
        if (newMovie.poster !== undefined) movieDetailsForFirebase.poster = newMovie.poster;
        if (newMovie.imdbID !== undefined) movieDetailsForFirebase.imdbID = newMovie.imdbID;
        if (newMovie.genre !== undefined) movieDetailsForFirebase.genre = newMovie.genre;
        if (newMovie.director !== undefined) movieDetailsForFirebase.director = newMovie.director;
        if (newMovie.plot !== undefined) movieDetailsForFirebase.plot = newMovie.plot;
        if (newMovie.rating !== undefined) movieDetailsForFirebase.rating = newMovie.rating;

        // Use the enhanced Firebase service that handles both data formats
        await watchlistService.addMovieToWatchlist(watchlistId, movieId, movieDetailsForFirebase);

        console.log('UserWatchlistDataContext: Movie successfully added to watchlist in Firebase:', {
          watchlistId,
          movieTitle: newMovie.title,
          movieId
        });

        touchWatchlistRecency(watchlistId);

        // Real-time listener will automatically reflect the change in the UI

      } catch (error) {
        console.error('UserWatchlistDataContext: Error adding movie to Firebase watchlist:', error);

        // Revert optimistic update if Firebase operation failed
        setUserWatchlists(prev => prev.map(list =>
          list.id === watchlistId
            ? {
              ...list,
              movieDetails: list.movieDetails.filter(m => m.id !== newMovie.id && m.imdbID !== newMovie.imdbID),
              updatedAt: new Date().toISOString()
            }
            : list
        ));

        setError(`Failed to add "${movie.title}" to watchlist: ${error}`);
        throw error;
      }
    }

    updateStats();
  };

  const removeMovieFromWatchlist = async (watchlistId: string, movieId: string) => {
    // Find the movie to be removed for potential rollback
    const watchlist = userWatchlists.find(list => list.id === watchlistId);
    const movieToRemove = watchlist?.movieDetails.find(movie => movie.id === movieId || movie.imdbID === movieId);

    // Update local state immediately for optimistic UI
    setUserWatchlists(prev => prev.map(list =>
      list.id === watchlistId
        ? {
          ...list,
          movieDetails: list.movieDetails.filter(movie => movie.id !== movieId && movie.imdbID !== movieId),
          updatedAt: new Date().toISOString()
        }
        : list
    ));

    // Persist to Firebase if user is authenticated
    if (user) {
      try {
        console.log('UserWatchlistDataContext: Removing movie from Firebase:', {
          watchlistId,
          movieId,
          movieTitle: movieToRemove?.title
        });

        // Use the enhanced Firebase service that handles both data formats
        await watchlistService.removeMovieFromWatchlist(watchlistId, movieId);

        console.log('UserWatchlistDataContext: Movie successfully removed from watchlist in Firebase:', movieId);

        // Real-time listener will automatically reflect the change in the UI

      } catch (error) {
        console.error('UserWatchlistDataContext: Error removing movie from Firebase watchlist:', error);

        // Revert optimistic update if Firebase operation failed
        if (movieToRemove) {
          setUserWatchlists(prev => prev.map(list =>
            list.id === watchlistId
              ? {
                ...list,
                movieDetails: [...list.movieDetails, movieToRemove],
                updatedAt: new Date().toISOString()
              }
              : list
          ));
        }

        setError('Failed to remove movie from watchlist');
        throw error;
      }
    }

    updateStats();
  };

  // Manual sync function to fix data consistency issues
  const syncWatchlistData = async (watchlistId: string) => {
    if (!user) return;

    try {
      console.log('UserWatchlistDataContext: Starting manual sync for watchlist:', watchlistId);

      const watchlist = userWatchlists.find(list => list.id === watchlistId);
      if (!watchlist) {
        console.warn('UserWatchlistDataContext: Watchlist not found for sync:', watchlistId);
        return;
      }

      // Use the enhanced Firebase sync function
      await movieService.syncWatchlistMovieData(watchlistId);

      console.log('UserWatchlistDataContext: Manual sync completed for watchlist:', watchlistId);

      // Refresh data after sync to ensure local state is updated
      await refreshData();

    } catch (error) {
      console.error('UserWatchlistDataContext: Error syncing watchlist data:', error);
      setError(`Failed to sync watchlist data: ${error}`);
    }
  };

  const moveMovieBetweenWatchlists = async (movieId: string, fromWatchlistId: string, toWatchlistId: string) => {
    const fromWatchlist = userWatchlists.find(list => list.id === fromWatchlistId);
    const movie = fromWatchlist?.movieDetails.find(m => m.id === movieId);

    if (!movie) return;

    await removeMovieFromWatchlist(fromWatchlistId, movieId);
    await addMovieToWatchlist(toWatchlistId, movie);
  };

  const updateMovieNote = async (watchlistId: string, movieId: string, note: string) => {
    if (!user) {
      throw new Error('User must be authenticated to update movie notes');
    }

    // Standardize the movie ID for consistent comparison
    const standardizedTargetId = getStandardizedMovieId({ id: movieId });
    console.log('updateMovieNote: Updating note for movie', { movieId, standardizedTargetId, watchlistId });

    try {
      // Update locally first for optimistic UI
      setUserWatchlists(prev => prev.map(list =>
        list.id === watchlistId
          ? {
            ...list,
            movieDetails: list.movieDetails.map(movie => {
              const standardizedMovieId = getStandardizedMovieId(movie);
              if (standardizedMovieId === standardizedTargetId) {
                console.log('updateMovieNote: Found matching movie, updating note', { movieId: movie.id, standardizedMovieId });
                return { ...movie, note: note.trim() };
              }
              return movie;
            }),
            updatedAt: new Date().toISOString()
          }
          : list
      ));

      // Update in Firebase
      await watchlistService.updateMovieNote(watchlistId, movieId, note.trim(), user.uid);

      touchWatchlistRecency(watchlistId);

      console.log('Successfully updated movie note:', movieId, 'in watchlist:', watchlistId);
    } catch (error) {
      console.error('Error updating movie note:', error);
      // Revert optimistic update on error - real-time listener will sync the correct state
      throw error;
    }
  };

  // Primary like/unlike functions
  const likeWatchlistLegacy = useCallback(async (watchlistId: string) => {
    if (!user) return;

    await watchlistService.likeWatchlist(user!.uid, watchlistId);
    invalidateWatchlistLikeCountCache(watchlistId);

    // Real-time listener will automatically reflect changes in liked watchlists
  }, [user]);

  const unlikeWatchlistLegacy = useCallback(async (watchlistId: string) => {
    if (!user) return;

    await watchlistService.unlikeWatchlist(user!.uid, watchlistId);
    invalidateWatchlistLikeCountCache(watchlistId);

    // Real-time listener will automatically reflect changes in liked watchlists
  }, [user]);

  const likeWatchlist = useCallback(async (watchlistId: string) => {
    if (!user) {
      console.error('User must be authenticated to like watchlists');
      return;
    }

    try {
      console.log('UserWatchlistDataContext: Attempting to like watchlist via Functions API:', watchlistId);
      const result: SocialActionResult = await functionsLikeWatchlist(watchlistId, 'like');

      if (result.success) {
        console.log('UserWatchlistDataContext: Like via Functions API successful, updating local state');

        // Update local state with the returned like count
        const updatedLikeCount = result.likesCount || 0;

        setUserWatchlists(prev => prev.map(watchlist =>
          watchlist.id === watchlistId
            ? { ...watchlist, followerCount: updatedLikeCount }
            : watchlist
        ));

        setLikedWatchlists(prev => {
          const existingIndex = prev.findIndex(w => w.id === watchlistId);
          if (existingIndex === -1) {
            // Add to liked watchlists if not already there
            const likedWatchlist = userWatchlists.find(w => w.id === watchlistId);
            if (likedWatchlist) {
              return [...prev, { ...likedWatchlist, followerCount: updatedLikeCount }];
            }
          } else {
            // Update existing entry
            return prev.map((watchlist, index) =>
              index === existingIndex
                ? { ...watchlist, followerCount: updatedLikeCount }
                : watchlist
            );
          }
          return prev;
        });

        setAllWatchlists(prev => prev.map(watchlist =>
          watchlist.id === watchlistId
            ? { ...watchlist, followerCount: updatedLikeCount }
            : watchlist
        ));

        // Broadcast like event for real-time updates across all components
        socialEventBroadcaster.broadcastLike(user.uid, watchlistId);

        // Invalidate the liked watchlists cache since it changed
        invalidateLikedWatchlistsCache(user.uid);
        invalidateWatchlistLikeCountCache(watchlistId);

        console.log('UserWatchlistDataContext: Local state updated after like and event broadcasted');
        return;
      } else {
        console.log('UserWatchlistDataContext: Functions API failed, falling back to legacy like');
        invalidateLikedWatchlistsCache(user.uid);
        invalidateWatchlistLikeCountCache(watchlistId);
        return likeWatchlistLegacy(watchlistId);
      }
    } catch (error) {
      console.error('UserWatchlistDataContext: Functions API error, falling back to legacy like:', error);
      invalidateLikedWatchlistsCache(user.uid);
      invalidateWatchlistLikeCountCache(watchlistId);
      return likeWatchlistLegacy(watchlistId);
    }
  }, [user, userWatchlists, likeWatchlistLegacy]);

  const unlikeWatchlist = useCallback(async (watchlistId: string) => {
    if (!user) {
      console.error('User must be authenticated to unlike watchlists');
      return;
    }

    try {
      console.log('UserWatchlistDataContext: Attempting to unlike watchlist via Functions API:', watchlistId);
      const result: SocialActionResult = await functionsLikeWatchlist(watchlistId, 'unlike');

      if (result.success) {
        console.log('UserWatchlistDataContext: Unlike via Functions API successful, updating local state');

        // Update local state with the returned like count
        const updatedLikeCount = result.likesCount || 0;

        setUserWatchlists(prev => prev.map(watchlist =>
          watchlist.id === watchlistId
            ? { ...watchlist, followerCount: updatedLikeCount }
            : watchlist
        ));

        setLikedWatchlists(prev => prev.filter(w => w.id !== watchlistId));

        setAllWatchlists(prev => prev.map(watchlist =>
          watchlist.id === watchlistId
            ? { ...watchlist, followerCount: updatedLikeCount }
            : watchlist
        ));

        // Broadcast unlike event for real-time updates across all components
        socialEventBroadcaster.broadcastUnlike(user.uid, watchlistId);

        // Invalidate the liked watchlists cache since it changed
        invalidateLikedWatchlistsCache(user.uid);
        invalidateWatchlistLikeCountCache(watchlistId);

        console.log('UserWatchlistDataContext: Local state updated after unlike and event broadcasted');
        return;
      } else {
        console.log('UserWatchlistDataContext: Functions API failed, falling back to legacy unlike');
        invalidateLikedWatchlistsCache(user.uid);
        invalidateWatchlistLikeCountCache(watchlistId);
        return unlikeWatchlistLegacy(watchlistId);
      }
    } catch (error) {
      console.error('UserWatchlistDataContext: Functions API error, falling back to legacy unlike:', error);
      invalidateLikedWatchlistsCache(user.uid);
      invalidateWatchlistLikeCountCache(watchlistId);
      return unlikeWatchlistLegacy(watchlistId);
    }
  }, [user, unlikeWatchlistLegacy]);

  // DEPRECATED: Follow functions now delegate to like functions for consistency
  const followWatchlist = useCallback(async (watchlistId: string) => {
    console.warn('followWatchlist is deprecated - using likeWatchlist instead');
    return likeWatchlist(watchlistId);
  }, [likeWatchlist]);

  const unfollowWatchlist = useCallback(async (watchlistId: string) => {
    console.warn('unfollowWatchlist is deprecated - using unlikeWatchlist instead');
    return unlikeWatchlist(watchlistId);
  }, [unlikeWatchlist]);

  const addCollaborator = async (watchlistId: string, userId: string, userName: string) => {
    if (!user) {
      throw new Error('User must be authenticated to add collaborators');
    }

    try {
      console.log('UserWatchlistDataContext: Adding collaborator:', { watchlistId, userId, userName });

      // Update Firebase first
      await firebaseService.addCollaboratorToWatchlist(watchlistId, userId, user.uid);

      // Update local state
      setUserWatchlists(prev => prev.map(list =>
        list.id === watchlistId
          ? {
            ...list,
            collaborators: [...(list.collaborators || []), userId],
            collaboratorNames: [...(list.collaboratorNames || []), userName],
            updatedAt: new Date().toISOString()
          }
          : list
      ));

      // Real-time listener will automatically reflect the change in the UI

      console.log('UserWatchlistDataContext: Collaborator added successfully');
    } catch (error) {
      console.error('UserWatchlistDataContext: Error adding collaborator:', error);
      throw error;
    }
  };

  const removeCollaborator = async (watchlistId: string, userId: string) => {
    if (!user) {
      throw new Error('User must be authenticated to remove collaborators');
    }

    try {
      console.log('UserWatchlistDataContext: Removing collaborator:', { watchlistId, userId });

      // Update Firebase first
      await firebaseService.removeCollaboratorFromWatchlist(watchlistId, userId, user.uid);

      // Update local state
      setUserWatchlists(prev => prev.map(list =>
        list.id === watchlistId
          ? {
            ...list,
            collaborators: list.collaborators?.filter(id => id !== userId),
            collaboratorNames: list.collaboratorNames?.filter((_, index) =>
              list.collaborators?.[index] !== userId
            ),
            updatedAt: new Date().toISOString()
          }
          : list
      ));

      // Real-time listener will automatically reflect the change in the UI

      console.log('UserWatchlistDataContext: Collaborator removed successfully');
    } catch (error) {
      console.error('UserWatchlistDataContext: Error removing collaborator:', error);
      throw error;
    }
  };

  const getCollaborativeWatchlists = async (): Promise<Watchlist[]> => {
    if (!user) {
      throw new Error('User must be authenticated to get collaborative watchlists');
    }

    try {
      console.log('UserWatchlistDataContext: Getting collaborative watchlists for user:', user.uid);

      const collaborativeWatchlists = await watchlistService.getCollaborativeWatchlists(user.uid);

      // Convert Firebase UserWatchlist format to local Watchlist format
      const formattedWatchlists: Watchlist[] = collaborativeWatchlists.map(watchlist => ({
        ...watchlist,
        createdByName: watchlist.createdBy, // Will be resolved later if needed
        followerCount: watchlist.followerCount || 0,
        collaboratorNames: watchlist.collaborators || [], // Will be resolved later if needed
        tags: [] // Add empty tags array since it's not in UserWatchlist but required in Watchlist
      }));

      console.log(`UserWatchlistDataContext: Found ${formattedWatchlists.length} collaborative watchlists`);
      return formattedWatchlists;
    } catch (error) {
      console.error('UserWatchlistDataContext: Error getting collaborative watchlists:', error);
      return [];
    }
  };

  const canUserEditWatchlist = async (watchlistId: string): Promise<boolean> => {
    if (!user) {
      return false;
    }

    try {
      return await watchlistService.canUserEditWatchlist(watchlistId, user.uid);
    } catch (error) {
      console.error('UserWatchlistDataContext: Error checking edit permissions:', error);
      return false;
    }
  };

  const getWatchlistById = (id: string): Watchlist | null => {
    return userWatchlists.find(list => list.id === id) ||
      likedWatchlists.find(list => list.id === id) ||
      allWatchlists.find(list => list.id === id) ||
      null;
  };

  const searchWatchlists = (query: string): Watchlist[] => {
    const lowerQuery = query.toLowerCase();
    return [...userWatchlists, ...likedWatchlists].filter(list =>
      list.name.toLowerCase().includes(lowerQuery) ||
      list.description?.toLowerCase().includes(lowerQuery) ||
      list.movieDetails.some(movie =>
        movie.title.toLowerCase().includes(lowerQuery) ||
        movie.genre?.toLowerCase().includes(lowerQuery)
      )
    );
  };

  const getWatchlistsByGenre = (genre: string): Watchlist[] => {
    return [...userWatchlists, ...likedWatchlists].filter(list =>
      list.movieDetails.some(movie =>
        movie.genre?.toLowerCase().includes(genre.toLowerCase())
      )
    );
  };

  const getMovieRecommendations = (watchlistId?: string): Movie[] => {
    // Simple recommendation based on genres in user's watchlists
    const watchlistsToAnalyze = watchlistId
      ? [getWatchlistById(watchlistId)].filter(Boolean) as Watchlist[]
      : userWatchlists;

    // This would be more sophisticated in a real app
    return [];
  };

  const getSimilarWatchlists = (watchlistId: string): Watchlist[] => {
    const targetWatchlist = getWatchlistById(watchlistId);
    if (!targetWatchlist) return [];

    // Find watchlists with similar movies or genres
    return allWatchlists.filter(list =>
      list.id !== watchlistId &&
      list.movieDetails.some(movie =>
        targetWatchlist.movieDetails.some(targetMovie =>
          targetMovie.genre === movie.genre
        )
      )
    ).slice(0, 5);
  };

  const getDefaultWatchlistSettings = (watchlistType: string) => {
    return defaultWatchlistSettings[watchlistType] || {};
  };

  const updateDefaultWatchlistSettings = async (watchlistType: string, updates: any) => {
    if (!user) return;
    await watchlistService.updateDefaultWatchlistSettings(user.uid, watchlistType, updates);
    setDefaultWatchlistSettings((prev: any) => ({
      ...prev,
      [watchlistType]: {
        ...prev[watchlistType],
        ...updates
      }
    }));
  };

  const value: UserWatchlistDataContextType = {
    userWatchlists,
    likedWatchlists,
    likedWatchlistsReady,
    allWatchlists,
    stats,
    isLoading,
    error,
    createWatchlist,
    updateWatchlist,
    deleteWatchlist,
    addMovieToWatchlist,
    removeMovieFromWatchlist,
    moveMovieBetweenWatchlists,
    updateMovieNote,
    likeWatchlist,
    unlikeWatchlist,
    addCollaborator,
    removeCollaborator,
    getCollaborativeWatchlists,
    canUserEditWatchlist,
    refreshData,
    getWatchlistById,
    searchWatchlists,
    getWatchlistsByGenre,
    getMovieRecommendations,
    getSimilarWatchlists,
    syncWatchlistData,
    getDefaultWatchlistSettings,
    updateDefaultWatchlistSettings,
    defaultWatchlistSettings,
  };

  return (
    <UserWatchlistDataContext.Provider value={value}>
      {children}
    </UserWatchlistDataContext.Provider>
  );
};

export const useUserWatchlistData = (): UserWatchlistDataContextType => {
  const context = useContext(UserWatchlistDataContext);
  if (context === undefined) {
    throw new Error('useUserWatchlistData must be used within a UserWatchlistDataProvider');
  }
  return context;
};

export default UserWatchlistDataContext; 